/**
 * Formatação de datas/horas para a interface — utilitário OFICIAL do projeto
 * (todos os módulos devem usar estas funções).
 *
 * Horário de Brasília = UTC−3 fixo (o Brasil não adota horário de verão desde
 * 2019). Usamos deslocamento fixo em vez de fuso IANA para que exibição e
 * entrada (campos "Personalizado") sejam sempre coerentes e testáveis.
 *
 * Regra de exibição: horários sempre com o rótulo do fuso — use as funções
 * `…Labeled` / `formatDateTimeBoth` ("22:30 (Brasília)", "01:30 UTC"). As
 * versões sem rótulo servem para quando o rótulo já aparece ao lado.
 */

export const MINUTE_MS = 60_000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

/** Deslocamento do horário de Brasília em relação ao UTC (ms). */
export const BRT_OFFSET_MS = -3 * HOUR_MS;

const pad = (n: number, len = 2) => String(n).padStart(len, '0');

const MONTHS_SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** Partes de data/hora de um instante, num deslocamento fixo em relação ao UTC. */
function parts(ms: number, offsetMs: number) {
  const d = new Date(ms + offsetMs);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
  };
}

/** "21:20" (Brasília). */
export function formatTimeBrt(ms: number): string {
  const p = parts(ms, BRT_OFFSET_MS);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** "00:20" (UTC). */
export function formatTimeUtc(ms: number): string {
  const p = parts(ms, 0);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** "14/09/2026" (Brasília). */
export function formatDateBrt(ms: number): string {
  const p = parts(ms, BRT_OFFSET_MS);
  return `${pad(p.day)}/${pad(p.month)}/${p.year}`;
}

/** "14 set" (Brasília). */
export function formatShortDateBrt(ms: number): string {
  const p = parts(ms, BRT_OFFSET_MS);
  return `${p.day} ${MONTHS_SHORT[p.month - 1]}`;
}

/** "14/09/2026 21:20" (Brasília). */
export function formatDateTimeBrt(ms: number): string {
  return `${formatDateBrt(ms)} ${formatTimeBrt(ms)}`;
}

/** "15/09 00:20" (UTC). */
export function formatDateTimeUtc(ms: number): string {
  const p = parts(ms, 0);
  return `${pad(p.day)}/${pad(p.month)} ${pad(p.hour)}:${pad(p.minute)}`;
}

/** "22:30 (Brasília)". */
export function formatTimeBrtLabeled(ms: number): string {
  return `${formatTimeBrt(ms)} (Brasília)`;
}

/** "01:30 UTC". */
export function formatTimeUtcLabeled(ms: number): string {
  return `${formatTimeUtc(ms)} UTC`;
}

/** "15 set" (UTC). */
export function formatShortDateUtc(ms: number): string {
  const p = parts(ms, 0);
  return `${p.day} ${MONTHS_SHORT[p.month - 1]}`;
}

/** A data em Brasília e em UTC são dias diferentes (21h–23h59 em Brasília)? */
export function utcDayDiffersFromBrt(ms: number): boolean {
  const b = parts(ms, BRT_OFFSET_MS);
  const u = parts(ms, 0);
  return b.day !== u.day || b.month !== u.month || b.year !== u.year;
}

/**
 * Hora UTC para exibir AO LADO de uma data de Brasília: "01:30 UTC", ou
 * "15 set, 01:30 UTC" quando em UTC já é outro dia.
 */
export function formatUtcBesideBrt(ms: number): string {
  return utcDayDiffersFromBrt(ms) ? `${formatShortDateUtc(ms)}, ${formatTimeUtcLabeled(ms)}` : formatTimeUtcLabeled(ms);
}

/** "14/09/2026 22:30 (Brasília)". */
export function formatDateTimeBrtLabeled(ms: number): string {
  return `${formatDateTimeBrt(ms)} (Brasília)`;
}

/** "15/09/2026 01:30 UTC". */
export function formatDateTimeUtcLabeled(ms: number): string {
  const p = parts(ms, 0);
  return `${pad(p.day)}/${pad(p.month)}/${p.year} ${pad(p.hour)}:${pad(p.minute)} UTC`;
}

/**
 * Data e hora nos dois fusos, numa linha:
 * "14/09/2026 22:30 (Brasília) · 15/09 01:30 UTC".
 * (A data UTC só aparece por inteiro se for de outro dia; senão "· 01:30 UTC".)
 */
export function formatDateTimeBoth(ms: number): string {
  const utc = parts(ms, 0);
  const utcText = utcDayDiffersFromBrt(ms)
    ? `${pad(utc.day)}/${pad(utc.month)} ${formatTimeUtcLabeled(ms)}`
    : formatTimeUtcLabeled(ms);
  return `${formatDateTimeBrtLabeled(ms)} · ${utcText}`;
}

/** "14/09 22:30" (Brasília, sem rótulo) — rótulos curtos de eixo/slider. */
export function formatDayTimeBrt(ms: number): string {
  const p = parts(ms, BRT_OFFSET_MS);
  return `${pad(p.day)}/${pad(p.month)} ${pad(p.hour)}:${pad(p.minute)}`;
}

/** "agora", "há 12 min", "há 3 h", "há 2 dias". */
export function formatRelative(ms: number, now: number = Date.now()): string {
  const diff = Math.max(0, now - ms);
  if (diff < MINUTE_MS) return 'agora';
  if (diff < HOUR_MS) return `há ${Math.floor(diff / MINUTE_MS)} min`;
  if (diff < DAY_MS) {
    const h = Math.floor(diff / HOUR_MS);
    const m = Math.floor((diff % HOUR_MS) / MINUTE_MS);
    return m >= 5 && h < 6 ? `há ${h} h ${m} min` : `há ${h} h`;
  }
  const d = Math.floor(diff / DAY_MS);
  return d === 1 ? 'há 1 dia' : `há ${d} dias`;
}

/** Valor para `<input type="datetime-local">` em horário de Brasília ("2026-09-14T21:20"). */
export function toBrtInputValue(ms: number): string {
  const p = parts(ms, BRT_OFFSET_MS);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** Lê um valor de `<input type="datetime-local">` (horário de Brasília) → epoch ms UTC. */
export function fromBrtInputValue(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number);
  const utc = Date.UTC(y, mo - 1, d, h, mi);
  return Number.isFinite(utc) ? utc - BRT_OFFSET_MS : null;
}

/** Instante UTC compacto para URLs: "202609150020". */
export function formatCompactUtc(ms: number): string {
  const p = parts(ms, 0);
  return `${p.year}${pad(p.month)}${pad(p.day)}${pad(p.hour)}${pad(p.minute)}`;
}

/** Lê "202609150020" (UTC) → epoch ms, ou null se inválido. */
export function parseCompactUtc(value: string): number | null {
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
  return Date.UTC(y, mo - 1, d, h, mi);
}
